/**
 * DiffPage Route Wrapper
 *
 * Extracts route parameters and auth context to render the DiffPage component.
 * Maps the URL pattern /studio/:verticalKey/:configType/diff to DiffPage props.
 */

import type { ReactElement } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { useAuthStore } from "../store/authStore";
import type { ConfigType } from "../types";

import { DiffPage } from "./DiffPage";

/** Get API base URL from environment */
function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;
  return envUrl ?? "http://localhost:8787";
}

export function DiffPageRoute(): ReactElement {
  const { verticalKey, configType } = useParams<{
    verticalKey: string;
    configType: string;
  }>();
  const navigate = useNavigate();

  if (!verticalKey || !configType) {
    return (
      <div className="flex h-full items-center justify-center text-foreground-muted">
        Invalid configuration path
      </div>
    );
  }

  const validConfigTypes: readonly ConfigType[] = [
    "schema",
    "eligibility",
    "ranking",
    "journey",
    "branding",
  ];

  if (!validConfigTypes.includes(configType as ConfigType)) {
    return (
      <div className="flex h-full items-center justify-center text-foreground-muted">
        Invalid config type: {configType}
      </div>
    );
  }

  return (
    <DiffPage
      verticalKey={verticalKey}
      configType={configType as ConfigType}
      apiBaseUrl={getApiBaseUrl()}
      getAuthToken={() => useAuthStore.getState().token ?? null}
      onPromotionSuccess={() => {
        navigate(`/studio/${verticalKey}/${configType}`);
      }}
      onRollbackSuccess={() => {
        navigate(`/studio/${verticalKey}/${configType}`);
      }}
    />
  );
}
